/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { captureException } from '@playbookux/playbook-web-core';
import {
  Container,
  Title,
  Loader,
  Video,
} from './styles';
import { RECORDER_STATUS } from '../../constants';
import StudyStatus from '../../study-status';

const MAX_ATTEMPTS = 3;

const DoNotClose = (props) => {
  const {
    callbackExtension,
    microphoneId,
    zoomClient,
    status,
    setStatus,
    setAbortStudy,
  } = props;
  const [zoomStream, setZoomStream] = useState();
  const [cloudRecording, setCloudRecording] = useState();
  const screenRef = useRef(null);

  // Confirmation before close tab
  const closeTabConfirmation = useCallback(() => {
    if (status === RECORDER_STATUS.RECORDING) {
      const confirmationMessage = '';

      return confirmationMessage;
    }
    return {};
  }, [status]);

  // ----- ZOOM STREAMING -----
  const startServices = useCallback(async (attempts = 0) => {
    try {
      const stream = zoomClient.getMediaStream();
      setZoomStream(stream);

      await stream.startAudio();

      await stream.switchMicrophone(microphoneId);
      const recorder = zoomClient.getRecordingClient();
      setCloudRecording(recorder);

      await recorder.startCloudRecording();
      setStatus(RECORDER_STATUS.RECORDING);
      callbackExtension('START_RECORDING');
    } catch (err) {
      if (err.reason === 'user deny screen share') {
        captureException('USER_DENY_SCREEN_SHARE_ERROR', err);
        setStatus(RECORDER_STATUS.ERROR);
        return;
      }
      if (attempts <= MAX_ATTEMPTS) {
        startServices(attempts + 1);
        return;
      }
      captureException('START_STREAM_SERVICES_ERROR', err);
      setStatus(RECORDER_STATUS.ERROR);
    }
  }, [callbackExtension, microphoneId, setStatus, zoomClient]);

  const handleStart = useCallback(async () => {
    setStatus(RECORDER_STATUS.LOADING);

    try {
      await startServices();
    } catch (err) {
      captureException('START_STREAM_ERROR', err);
      setStatus(RECORDER_STATUS.ERROR);
    }
  }, [setStatus, startServices]);

  const handleStop = useCallback(async () => {
    try {
      setStatus(RECORDER_STATUS.COMPLETED);
      await cloudRecording?.stopCloudRecording();
      await zoomStream.stopShareScreen();
      await zoomClient.leave();
    } catch (err) {
      setStatus(RECORDER_STATUS.ERROR);
      captureException('STOP_STREAM_ERROR', err);
    }
  }, [cloudRecording, setStatus, zoomClient, zoomStream]);

  const handlePause = useCallback(async () => {
    try {
      await cloudRecording?.pauseCloudRecording();
      setStatus(RECORDER_STATUS.PAUSED);
    } catch (err) {
      setStatus(RECORDER_STATUS.ERROR);
      captureException('PAUSE_STREAM_ERROR', err);
    }
  }, [cloudRecording, setStatus]);

  const handleResume = useCallback(async () => {
    try {
      await cloudRecording?.resumeCloudRecording();
      setStatus(RECORDER_STATUS.RECORDING);
    } catch (err) {
      setStatus(RECORDER_STATUS.ERROR);
      captureException('RESUME_STREAM_ERROR', err);
    }
  }, [cloudRecording, setStatus]);

  useEffect(() => {
    if (status === RECORDER_STATUS.RECORDING) {
      window.onbeforeunload = (e) => {
        const event = e || window.event;
        // For IE and Firefox prior to version 4
        if (event) {
          event.returnValue = 'Sure?';
        }
        // For Safari
        return 'Sure?';
      };
    } else {
      window.onbeforeunload = null;
    }
  }, [status]);

  useEffect(() => {
    const stopAbortedStudy = async () => {
      await cloudRecording?.stopCloudRecording();
      await zoomStream?.stopShareScreen();
    };

    // FIXME: add a specific validation for errors
    if (status === RECORDER_STATUS.ABORTED || status === RECORDER_STATUS.ERROR) {
      stopAbortedStudy();
      callbackExtension('ABORT_STUDY');
    }
  }, [callbackExtension, status]);

  useEffect(() => {
    document.addEventListener('abortStudyEvent', setAbortStudy);

    document.addEventListener('startZoomEvent', handleStart);
    document.addEventListener('pauseZoomEvent', handlePause);
    document.addEventListener('resumeZoomEvent', handleResume);
    document.addEventListener('stopZoomEvent', handleStop);

    window.addEventListener('beforeunload', closeTabConfirmation);

    return () => {
      document.removeEventListener('abortStudyEvent', setAbortStudy);

      document.removeEventListener('startZoomEvent', handleStart);
      document.removeEventListener('pauseZoomEvent', handlePause);
      document.removeEventListener('resumeZoomEvent', handleResume);
      document.removeEventListener('stopZoomEvent', handleStop);

      window.removeEventListener('beforeunload', closeTabConfirmation);
    };
  }, [closeTabConfirmation, handlePause, handleResume, handleStop, handleStart, setAbortStudy]);

  switch (status) {
    case RECORDER_STATUS.ERROR: {
      return (
        <Container>
          <Title>ERROR: PLEASE, CLOSE THIS TAB AND TRY AGAIN</Title>
        </Container>
      );
    }

    case RECORDER_STATUS.LOADING: {
      return (
        <Container>
          <Loader size="huge" data-loader="unmoderated-session-loader" />
          <Video ref={screenRef} />
        </Container>
      );
    }

    case RECORDER_STATUS.IDLE: {
      return (
        <StudyStatus
          screenRef={screenRef}
          title="Do not close this tab."
          subtitle="Please keep this tab open while completing this study.
          If you close out of this page, your session will end."
        />
      );
    }

    case RECORDER_STATUS.ABORTED: {
      return (
        <StudyStatus
          screenRef={screenRef}
          title="You’ve stopped screen sharing"
          subtitle="You’ve ended this test by stopping your screen share."
        />
      );
    }

    case RECORDER_STATUS.PAUSED: {
      return (
        <StudyStatus
          screenRef={screenRef}
          title="Your study is paused"
          subtitle="Click the play button to resume the study."
        />
      );
    }

    case RECORDER_STATUS.RECORDING: {
      return (
        <StudyStatus
          screenRef={screenRef}
          title="You are sharing your screen"
          subtitle="Please keep this tab open while completing this study.
          If you close out of this page, your session will end."
        />
      );
    }

    case RECORDER_STATUS.COMPLETED: {
      return (
        <StudyStatus
          screenRef={screenRef}
          title="Your study is complete"
          subtitle="You can close out of this screen."
        />
      );
    }

    default: {
      return null;
    }
  }
};

DoNotClose.propTypes = {
  setAbortStudy: PropTypes.func.isRequired,
  setStatus: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  microphoneId: PropTypes.string.isRequired,
  callbackExtension: PropTypes.func.isRequired,
  zoomClient: PropTypes.shape({
    on: PropTypes.func,
    off: PropTypes.func,
    leave: PropTypes.func,
    getRecordingClient: PropTypes.func,
    getMediaStream: PropTypes.func,
  }).isRequired,
};

export default DoNotClose;
